import { useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import {
  type CarsAppLoadedRawEvent,
  type CarsRawEvent,
  CarsRawEventName,
  type CarHTSEvent,
  type CarEntry,
  CarEventName,
  CARS_HTS_EVENT_TRANSFORMER_HOOKS,
  defaultCarsEntryEventTransformer,
  type Providers,
  type CarsState,
} from "@apac/cars";
import { useEventTracking } from "@b2bportal/core-tracking";
import type { UseInstantTransformEvent } from "@b2bportal/core-tracking";
import type { EventTransformerHooks } from "@b2bportal/core-tracking";
import { Funnel } from "@b2bportal/core-types";
import { useTrackFunnelEntry } from "./hooks";

/**
 * Hook to get providers (tracking attributes + redux state) for cars transformers.
 */
function useGetProviders(): () => Providers {
  const { getAllAttributes } = useEventTracking();
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(
    () => ({
      trackingAttributes: getAllAttributes(),
      reduxState: reduxContext.store.getState() as CarsState,
    }),
    [reduxContext.store, getAllAttributes]
  );
}

/**
 * RBC-specific transformer for cars_entry event.
 * Fires on every funnel transition (not just once per session).
 *
 * Reuses `defaultCarsEntryEventTransformer` from apac/cars to get all properties.
 */
export const useRbcCarAppLoadedTransformer: UseInstantTransformEvent<
  CarsAppLoadedRawEvent,
  CarEntry
> = () => {
  const shouldTrack = useTrackFunnelEntry(Funnel.Ground);
  const getProviders = useGetProviders();

  return useCallback(() => {
    if (!shouldTrack) {
      return undefined;
    }

    const htsEvent = defaultCarsEntryEventTransformer<CarEntry>(
      CarEventName.cars_entry,
      getProviders(),
      {}
    );

    return htsEvent;
  }, [shouldTrack, getProviders]);
};

/**
 * RBC-specific cars event transformer hooks (overrides only)
 */
export const RBC_CARS_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  CarsRawEvent,
  CarHTSEvent
> = {
  [CarsRawEventName.CarAppLoaded]: [useRbcCarAppLoadedTransformer],
};

/**
 * All RBC cars transformers - core transformers with RBC overrides
 * RBC transformers REPLACE core transformers for the same event (not merge)
 */
export const ALL_RBC_CARS_TRANSFORMERS: EventTransformerHooks<
  CarsRawEvent,
  CarHTSEvent
> = {
  // Start with all core transformers
  ...CARS_HTS_EVENT_TRANSFORMER_HOOKS,
  // Override with RBC-specific transformers (replaces, not merges)
  ...RBC_CARS_HTS_EVENT_TRANSFORMER_HOOKS,
};
